import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { getProducts } from '@/lib/cms/getProducts'
import { hydra } from '@/lib/services/Hydra'
import { jeeves } from '@/lib/services/Jeeves'
import { assignPricingToVariation } from '@/lib/utils/api/assignPricingToVariation'
import type { SupportedAlpha3CountryCode } from '@/lib/utils/localization/getSupportedAlpha3CountryCodes'
import { toAlpha2 } from '@/lib/utils/localization/toAlpha2'
import useGlobalStore from '@/store/useGlobalStore'
import { useParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'

export const useGetSubscriptions = () => {
  const {
    user: { token },
    app: { locale, alpha2 },
    setSubscriptions,
  } = useGlobalStore()

  const params = useParams()
  const alpha3 = params?.alpha3

  const getSubscriptions = useCallback(async () => {
    if (!token) return
    if (!locale) return
    if (!alpha3) return

    // Makes sure the URL market is the same as the locale set in the global store,
    // Having it out of sync causes the app to run this useEffect multiple times
    if (
      locale.split('-')[1].toLowerCase() !==
      toAlpha2(alpha3 as SupportedAlpha3CountryCode)?.toLowerCase()
    ) {
      return
    }

    setSubscriptions({
      isLoading: true,
      error: null,
    })

    try {
      const [subscriptionsResponse, products] = await Promise.all([
        hydra.getSubscriptions({
          hydraToken: token,
        }),
        getProducts({
          locale,
        }),
      ])

      if (!subscriptionsResponse || !products) {
        setSubscriptions({
          error: {
            error: {
              message: 'Internal error, unable to get subscriptions data.',
            },
          },
        })

        return
      }

      if ('error' in products) {
        setSubscriptions({
          error: {
            error: {
              message: 'Internal error, unable to get subscriptions data.',
            },
          },
        })

        return
      }

      const allProductSkus = products
        .flatMap(
          (product) =>
            product.variations
              ?.map((variation) => {
                const { sku } = variation

                return sku || ''
              })
              .filter((sku) => sku.trim() !== ''),
        )
        // Remove undefined SKUs, otherwise could result in no products being displayed.
        .filter(Boolean)

      const pricingData = await jeeves.getProductPricing({
        market: alpha2,
        skus: allProductSkus,
      })

      if (!pricingData) {
        setSubscriptions({
          error: {
            error: {
              message: 'Failed to load pricing data.',
            },
          },
        })

        return
      }

      const productsWithAssignedPricing = products
        .map((product) =>
          assignPricingToVariation({
            productData: product,
            pricingData,
          }),
        )
        .filter((product) => product !== null) as CosmicProductType[]

      const subscriptionsWithProductDataAddedToTheLines =
        subscriptionsResponse.items.map((sub) => {
          const modifiedLines = sub.lines.items.map((line) => {
            const product = productsWithAssignedPricing.find(
              (p) =>
                p?.variations?.some(
                  (variant) => variant.sku === line.item.id.unicity.toString(),
                ),
            )

            return {
              ...line,
              productData: product,
            }
          })

          return {
            ...sub,
            lines: {
              ...sub.lines,
              items: modifiedLines,
            },
          }
        })

      setSubscriptions({
        items: subscriptionsWithProductDataAddedToTheLines.slice(0, 50),
      })
    } catch (error) {
      console.log(error)

      setSubscriptions({
        error: {
          error: {
            message: 'An error occurred while fetching subscriptions',
            code: 500,
          },
        },
      })
    } finally {
      setSubscriptions({
        isLoading: false,
      })
    }
  }, [token, locale, alpha3])

  useEffect(() => {
    ;(async () => {
      await getSubscriptions()
    })().catch(() => {})
  }, [getSubscriptions])
}
